import {SlotsPlaceholder} from '@wix/widget-plugins-ooi';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {ProductType} from '@wix/wixstores-client-core/dist/es/src/types';
import {isOptionRequired} from '@wix/wixstores-client-core/dist/es/src/productOptions/productUtils';
import {VIEW_MORE_OPTIONS_URL_FRAGMENT} from '@wix/wixstores-client-storefront-sdk/dist/es/src/constants';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src';
import classNames from 'classnames';
import React from 'react';
import stylesParams from '../../../components/ProductPage/stylesParams';
import {ProductPageSlotIds, TextualOptionType, UserInputType} from '../../constants';
import {ProvidedGlobalProps, withGlobalProps} from '../../providers/globalPropsProvider';
import {SPECS} from '../../specs';
import {IProductDTO} from '../../types/app-types';
import {ConditionalRender} from '../ConditionalRender/ConditionalRender';
import {Cell} from '../Layouts/Cell/Cell';
import {OptionsButtons} from './OptionsButton/OptionsButtons';
import {OptionsDropdown} from './OptionsDropdown/OptionsDropdown';
import {ProductColors} from './ProductColors/ProductColors';
import {ProductCustomText} from './ProductCustomText/ProductCustomText';
import s from './ProductOptions.scss';
import {ProductQuantity} from './ProductQuantity/ProductQuantity';
import {StockIndicator} from './StockIndicator/StockIndicator';
import {SubscriptionPlans} from './SubscriptionPlans/SubscriptionPlans';

export interface ProductOptionsProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  quickViewTextualOptionType?: TextualOptionType;
  className?: string;
}

export const ProductOptionsDataHook = {
  Root: 'product-options',
  Inputs: 'product-options-inputs',
  StockIndicator: 'stock-indicator',
  OutOfStockIndicator: 'out-of-stock-indicator',
};

interface ProductOptionsState {
  addedToWishlist: boolean;
}

class ProductOptionsComponent extends React.Component<ProductOptionsProps, ProductOptionsState> {
  public state = {
    addedToWishlist: false,
  };

  private colorPickerCounter = 0;

  public handleUserInput =
    (inputType: UserInputType, index: number) =>
    (data: string | number): void => {
      const {handleUserInput, validateUserInputsChanges} = this.props.globals;

      /* istanbul ignore else: todo: test */
      if (typeof handleUserInput === 'function') {
        handleUserInput(inputType, data, index);
      }

      validateUserInputsChanges();
    };

  private renderOptions(): JSX.Element {
    const {product} = this.props.globals;
    this.colorPickerCounter = 0;

    return <>{product.options.map(this.renderOption)}</>;
  }

  private readonly renderOption = (option: IProductDTO['options'][number], index: number): JSX.Element => {
    const {styles, experiments, useExperiments, isCombinedListing, productGroup, product} = this.props.globals;
    const textualOptionType =
      this.props.quickViewTextualOptionType ?? styles.get(stylesParams.productPage_textualOptionType);
    const isButtonsOptionEnabled = experiments.allowProductPageButtonsOption;
    const isRequired = isOptionRequired(product, option);

    if (isCombinedListing) {
      const isThisOptionGroupedBy = option.id === productGroup?.groupingCustomizationId;

      if (!isThisOptionGroupedBy && !product.isInStock) {
        return null;
      }
    }

    if (option.optionType === ProductOptionType.COLOR) {
      this.colorPickerCounter++;
      const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StoresStickyAddToCart);
      let props = {};
      if (isStickyAddToCartEnabled) {
        props = {id: `${VIEW_MORE_OPTIONS_URL_FRAGMENT}-${this.colorPickerCounter}`};
      } else {
        props = {colorPickerCounter: this.colorPickerCounter};
      }

      return (
        <Cell className={s.colors} key={`product-options-${index}`}>
          <ProductColors optionIndex={index} isRequired={isRequired} {...props} />
        </Cell>
      );
    }
    //get query params from store, and check if we are in quick view, and check if the param is buttons or dropdown
    return (
      <Cell key={`product-options-${index}`}>
        {isButtonsOptionEnabled && textualOptionType === TextualOptionType.BUTTONS ? (
          <OptionsButtons optionIndex={index} isRequired={isRequired} />
        ) : (
          <OptionsDropdown optionIndex={index} isRequired={isRequired} />
        )}
      </Cell>
    );
  };

  private renderStockIndicator() {
    const {
      t,
      globals: {remainingItemCount, useExperiments},
    } = this.props;

    const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StoresStickyAddToCart);
    if (isStickyAddToCartEnabled) {
      return (
        <div className={s.stockIndicator}>
          <StockIndicator />
        </div>
      );
    }

    if (remainingItemCount > 0) {
      return (
        <ConditionalRender by="productPage_stockIndicator">
          <div className={s.stockIndicator} data-hook={ProductOptionsDataHook.StockIndicator}>
            {t('productPage.lowStockMessage.label', {Inventory: remainingItemCount})}
          </div>
        </ConditionalRender>
      );
    } else if (remainingItemCount === 0) {
      return (
        <div className={s.outOfStockIndicator} data-hook={ProductOptionsDataHook.OutOfStockIndicator}>
          {t('PRODUCT_OUT_OF_STOCK_BUTTON')}
        </div>
      );
    }
    return null;
  }

  private renderQuantity() {
    const {
      globals: {product, userInputs, productGroup},
    } = this.props;
    const isPhysicalProduct = product.productType === ProductType.PHYSICAL;
    const quantity = userInputs[UserInputType.Quantity][0];

    if (!isPhysicalProduct) {
      return null;
    }

    if (productGroup && !product.isInStock) {
      return null;
    }

    return (
      <ConditionalRender by="productPage_productQuantity">
        <Cell className={s.quantity}>
          <ProductQuantity value={quantity} onChange={this.handleUserInput(UserInputType.Quantity, 0)} />
        </Cell>
      </ConditionalRender>
    );
  }

  private renderCustomText() {
    const {
      globals: {
        product: {customTextFields},
        useExperiments,
      },
    } = this.props;

    const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StoresStickyAddToCart);

    return customTextFields.map((customTextField, index) => {
      let handleOnChangeProps = {};
      if (!isStickyAddToCartEnabled) {
        handleOnChangeProps = {handleOnChange: this.handleUserInput(UserInputType.Text, index)};
      }

      return (
        <Cell key={index}>
          <ProductCustomText
            errorIndex={index}
            title={customTextField.title}
            isRequired={customTextField.isMandatory}
            maxLength={customTextField.inputLimit}
            {...handleOnChangeProps}
          />
        </Cell>
      );
    });
  }

  private renderSubscriptionPlans(): JSX.Element {
    return (
      <Cell>
        <SubscriptionPlans />
      </Cell>
    );
  }

  public render(): JSX.Element {
    const {
      globals: {
        product,
        isPreOrderState,
        product: {customTextFields},
        shouldShowSubscriptionPlans,
        shouldShowUserInputs,
        experiments: {renderProductPageSlots},
        slots,
      },
      className,
    } = this.props;
    const isPluginInstalledInDetails5Slot = !!slots?.[ProductPageSlotIds.ProductPageDetails5];
    const isPluginInstalledInDetails6Slot = !!slots?.[ProductPageSlotIds.ProductPageDetails6];
    const isPluginInstalledInDetails7Slot = !!slots?.[ProductPageSlotIds.ProductPageDetails7];

    return (
      <div
        data-hook={ProductOptionsDataHook.Root}
        className={classNames(s.productOptionsContainer, className)}
        key={`product-option-${product.id}-${product.currency}`}>
        {shouldShowUserInputs && (
          <div data-hook={ProductOptionsDataHook.Inputs}>
            {this.renderOptions()}
            {renderProductPageSlots && (
              <div className={classNames({[s.productPageSlot]: isPluginInstalledInDetails5Slot})}>
                <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails5} />
              </div>
            )}
            {customTextFields && this.renderCustomText()}
            {renderProductPageSlots && (
              <div className={classNames({[s.productPageSlot]: isPluginInstalledInDetails6Slot})}>
                <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails6} />
              </div>
            )}
            {this.renderQuantity()}
            {renderProductPageSlots && (
              <div className={classNames({[s.productPageSlot]: isPluginInstalledInDetails7Slot})}>
                <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails7} />
              </div>
            )}
            {isPreOrderState ? null : this.renderStockIndicator()}
          </div>
        )}
        {shouldShowSubscriptionPlans && this.renderSubscriptionPlans()}
      </div>
    );
  }
}

export const ProductOptions: React.FC<ProductOptionsProps> = withGlobalProps(
  withTranslations('globals.texts')(ProductOptionsComponent)
);
